import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as api from './deliveryFunctionNetworkInterface.api';
import { extractErrorMessage } from '../../utils';
import {
  SelectDeliveryFunctionNetworkInterfaceAction,
  DeleteDeliveryFunctionNetworkInterfaceThunkAction,
  UpdateDeliveryFunctionNetworkInterfaceThunkAction,
  CreateDeliveryFunctionNetworkInterfaceThunkAction,
  DeliveryFunctionNetworkInterfaceActionTypes,
  DeliveryFunctionNetworkInterface,
  SetDeliveryFunctionNetworkInterfaceLoadingAction,
  GET_DELIVERY_FUNCTION_NETWORK_INTERFACE_LIST,
  GET_DELIVERY_FUNCTION_NETWORK_INTERFACE,
  CREATE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
  UPDATE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
  DELETE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
  SELECT_DELIVERY_FUNCTION_NETWORK_INTERFACE,
  OPEN_DELIVERY_FUNCTION_NETWORK_INTERFACE_CREATE_POPUP,
  CLOSE_DELIVERY_FUNCTION_NETWORK_INTERFACE_CREATE_POPUP,
  OPEN_DELIVERY_FUNCTION_NETWORK_INTERFACE_EDIT_POPUP,
  CLOSE_DELIVERY_FUNCTION_NETWORK_INTERFACE_EDIT_POPUP,
  SET_DELIVERY_FUNCTION_NETWORK_INTERFACE_LOADING,
} from './deliveryFunctionNetworkInterface.types';
import { getSelectedDeliveryFunctionNetworkInterface } from './deliveryFunctionNetworkInterface.selectors';

export function openDeliveryFunctionNetworkInterfaceCreatePopup(payload: DeliveryFunctionNetworkInterface): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_DELIVERY_FUNCTION_NETWORK_INTERFACE_CREATE_POPUP,
      payload
    });
  };
}
export function closeDeliveryFunctionNetworkInterfaceCreatePopup(): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_DELIVERY_FUNCTION_NETWORK_INTERFACE_CREATE_POPUP,
    });
  };
}

export function openDeliveryFunctionNetworkInterfaceEditPopup(): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_DELIVERY_FUNCTION_NETWORK_INTERFACE_EDIT_POPUP,
    });
  };
}

export function closeDeliveryFunctionNetworkInterfaceEditPopup(): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_DELIVERY_FUNCTION_NETWORK_INTERFACE_EDIT_POPUP,
    });
  };
}

export function setDeliveryFunctionNetworkInterfaceLoadingAction(
  isLoading: boolean,
): SetDeliveryFunctionNetworkInterfaceLoadingAction {
  return {
    type: SET_DELIVERY_FUNCTION_NETWORK_INTERFACE_LOADING,
    payload: { isLoading },
  };
}

export function getDeliveryFunctionNetworkInterfaceList(
  deliveryFunctionId: string,
): DeliveryFunctionNetworkInterfaceActionTypes {
  return {
    type: GET_DELIVERY_FUNCTION_NETWORK_INTERFACE_LIST,
    payload: api.getDeliveryFunctionNetworkInterfaceList(deliveryFunctionId),
  };
}

export function getDeliveryFunctionNetworkInterface(
  deliveryFunctionId: string, id: string,
): DeliveryFunctionNetworkInterfaceActionTypes {
  return {
    type: GET_DELIVERY_FUNCTION_NETWORK_INTERFACE,
    payload: api.getDeliveryFunctionNetworkInterface(deliveryFunctionId, id),
  };
}

export function createDeliveryFunctionNetworkInterface(
  deliveryFunctionId: string,
  body: WithAuditDetails<DeliveryFunctionNetworkInterface>,
): CreateDeliveryFunctionNetworkInterfaceThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
        payload: api.createDeliveryFunctionNetworkInterface(deliveryFunctionId, body),
      });

      dispatch(getDeliveryFunctionNetworkInterfaceList(deliveryFunctionId));
      dispatch(closeDeliveryFunctionNetworkInterfaceCreatePopup());
      showSuccessMessage(i18n.t('collectionFunctionGroup.create.success', { name: body.data?.interface }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.create.failed', { name: body.data?.interface, error: errorMessage }));
    }
  };
}

export function createDeliveryFunctionNetworkInterfaceFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<DeliveryFunctionNetworkInterface>,
): CreateDeliveryFunctionNetworkInterfaceThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setDeliveryFunctionNetworkInterfaceLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
        payload: api.createDeliveryFunctionNetworkInterface(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('collectionFunctionGroup.create.success', { name: body.data?.interface }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.create.failed', { name: body.data?.interface, error: errorMessage }));

    } finally {
      dispatch(setDeliveryFunctionNetworkInterfaceLoadingAction(false));
      dispatch(getDeliveryFunctionNetworkInterfaceList(deliveryFunctionId));
    }
  };
}

export function updateDeliveryFunctionNetworkInterface(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<DeliveryFunctionNetworkInterface>,
  history: History,
): UpdateDeliveryFunctionNetworkInterfaceThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
        payload: api.updateDeliveryFunctionNetworkInterface(deliveryFunctionId, id, body),
      });

      dispatch(getDeliveryFunctionNetworkInterfaceList(deliveryFunctionId));
      dispatch(closeDeliveryFunctionNetworkInterfaceEditPopup());
      showSuccessMessage(i18n.t('collectionFunctionGroup.edit.success', { name: body.data?.interface }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.edit.failed', { name: body.data?.interface, error: errorMessage }));
    }
  };
}

export function deleteDeliveryFunctionNetworkInterface(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteDeliveryFunctionNetworkInterfaceThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedDeliveryFunctionNetworkInterface] = getSelectedDeliveryFunctionNetworkInterface(getState());

    try {
      await dispatch({
        type: DELETE_DELIVERY_FUNCTION_NETWORK_INTERFACE,
        payload: api.deleteDeliveryFunctionNetworkInterface(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('collectionFunctionGroup.delete.success', { name: selectedDeliveryFunctionNetworkInterface?.interface }), 5000);
    } catch (e) {
      showError(i18n.t('collectionFunctionGroup.delete.failed', { name: selectedDeliveryFunctionNetworkInterface?.interface }));
    } finally {
      dispatch(getDeliveryFunctionNetworkInterfaceList(deliveryFunctionId));
    }
  };
}

export function selectDeliveryFunctionNetworkInterface(
  collectionFunction: DeliveryFunctionNetworkInterface[],
): SelectDeliveryFunctionNetworkInterfaceAction {
  return {
    type: SELECT_DELIVERY_FUNCTION_NETWORK_INTERFACE,
    payload: collectionFunction,
  };
}
